import { resolveId } from '@medplum/core'
import type { Reference, ServiceRequest, Task } from '@medplum/fhirtypes'
import { IDT } from '@seen/core/models/care-teams/idt'
import { DOCUMENT_CATEGORY_OPTIONS } from '@seen/core/models/document-references/constants'
import { ExtendedDocumentReference } from '@seen/core/models/document-references/documentReferences'
import type { Order } from '@seen/core/models/orders/Order'
import { Signature } from '@seen/core/models/signature'
import { DocumentReviewTask } from '@seen/core/models/tasks/standard/documentReviewTask'
import { StandardTask } from '@seen/core/models/tasks/standard/standardTask'
import { Role } from '@seen/core/roles'
import { queryTypesToString } from '@seen/core/search'
import { parseReferenceString } from '@seen/core/utils'
import { Button, ButtonGroup } from '@seen/ui'
import {
  IconAlertTriangle,
  IconScissors,
  IconSparkles,
  IconTrash,
} from '@tabler/icons-react'
import pluralize from 'pluralize'
import type { JSX, ReactNode } from 'react'
import { lazy, Suspense, useCallback, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { ComboboxButton } from '#src/components/ComboboxButton.tsx'
import {
  Combobox,
  type SuggestedOption,
} from '#src/components/core/combobox/Combobox.tsx'
import {
  ComboboxMulti,
  type SuggestedMultiOption,
} from '#src/components/core/combobox/ComboboxMulti.tsx'
import { MultiSelect } from '#src/components/core/combobox/MultiSelect.tsx'
import {
  Field,
  FieldContent,
  FieldError as FieldErrorComponent,
  FieldLabel,
} from '#src/components/core/form/Field.tsx'
import { Form } from '#src/components/core/form/Form.tsx'
import { FormField } from '#src/components/core/form/FormField.tsx'
import { Tooltip } from '#src/components/core/tooltip/Tooltip.tsx'
import { TooltipContent } from '#src/components/core/tooltip/TooltipContent.tsx'
import { TooltipTrigger } from '#src/components/core/tooltip/TooltipTrigger.tsx'
import { useFolders } from '#src/components/document-reference/useFolders.ts'
import { ParticipantNameplate } from '#src/components/nameplates/ParticipantNameplate.tsx'
import { UserPlaceholderNameplate } from '#src/components/nameplates/UserPlaceholderNameplate.tsx'
import { ParticipantOption } from '#src/components/options/ParticipantOption.tsx'
import { PractitionerOption } from '#src/components/options/PractitionerOption.tsx'
import { OrderOption } from '#src/components/OrderOption.tsx'
import { OwnerComboboxButton } from '#src/components/OwnerComboboxButton.tsx'
import { TaskPriorityCombobox } from '#src/components/task/TaskPriorityCombobox.tsx'
import { TaskPriorityIcon } from '#src/components/task/TaskPriorityIcon.tsx'
import { createTaskFilter } from '#src/hooks/inbox/filter.ts'
import { useMarkRelatedInboxItemsAsDone } from '#src/hooks/inbox/useMarkRelatedInboxItemsAsDone.ts'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useCurrentPractitionerPerms } from '#src/hooks/useCurrentPractitionerPerms.ts'
import { useDeleteConfirmationStore } from '#src/hooks/useDeleteConfirmationStore.ts'
import { useDidChange } from '#src/hooks/useDidChange.ts'
import type { DocumentReferenceFormValues } from '#src/hooks/useDocumentForm.ts'
import { folderFromId, useDocumentForm } from '#src/hooks/useDocumentForm.ts'
import { useTransactionMutation } from '#src/hooks/useFhirBundleMutation.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { useParticipants } from '#src/hooks/useParticipants.ts'
import { usePatchMutation } from '#src/hooks/usePatchMutation.ts'
import { usePractitionersInternal } from '#src/hooks/usePractitionersInternal.ts'
import { useSearch } from '#src/hooks/useSearch.ts'
import { useSearchOne } from '#src/hooks/useSearchOne.ts'

// Lazy-load SplitDocumentOverlay to keep `react-pdf`/`pdfjs-dist` out of the
// static module graph. Those libraries touch browser-only APIs (`DOMMatrix`)
// at module-init time and crash when transitively SSR'd by non-browser apps
// that import from `@seen/react`.
const SplitDocumentOverlay = lazy(() =>
  import('#src/components/document-inbox/SplitDocumentOverlay.tsx').then(
    (m) => ({
      default: m.SplitDocumentOverlay,
    })
  )
)

// Sentinel for "no folder" — ComboboxButton has no clear action, so the
// unfiled state needs a selectable option of its own.
const OTHER_FOLDER_VALUE = 'other'

// Sign, Mark as Filed, and Assign to PCP for Review all write the document
// through this, so a form field missing here saves as a silent no-op — the
// action succeeds and the value is dropped. Exported so a test can hold the
// full set of form fields to what actually gets written.
export function applyFormValuesToDocument(
  doc: ExtendedDocumentReference,
  formValues: DocumentReferenceFormValues,
  availableOrders: Order[] | undefined
): void {
  doc.subject = formValues.subject ?? undefined
  doc.categories = formValues.categories ?? undefined
  const selectedOrders =
    formValues.orders
      ?.map(
        (orderId) => availableOrders?.find((o) => o.id === orderId)?.reference
      )
      .filter(
        (
          order
        ): order is Reference<ServiceRequest> & {
          reference: string
        } => !!order?.reference
      ) ?? []
  doc.orders = selectedOrders.length ? selectedOrders : undefined
  doc.folder = folderFromId(formValues.folderId)
  doc.markAsConfirmed()
}

function SuggestedOptionTooltip({
  confidence,
  reason,
}: {
  confidence?: number
  reason?: string
}): ReactNode {
  return (
    <Tooltip>
      <TooltipTrigger asChild onClick={(e) => e.stopPropagation()}>
        <span className="inline-flex">
          <IconSparkles className="size-4 text-green-600" />
        </span>
      </TooltipTrigger>
      <TooltipContent side="right" className="max-w-xs">
        <div className="flex flex-col gap-1">
          {confidence !== undefined && (
            <span className="font-medium">
              {Math.round(confidence * 100)}% confidence
            </span>
          )}
          {reason && <span>{reason}</span>}
        </div>
      </TooltipContent>
    </Tooltip>
  )
}

interface DocumentReviewToolbarProps {
  docRef: ExtendedDocumentReference
  task: DocumentReviewTask
  onUpdate: () => void
  onComplete: () => void
}

// Returns the owner reference to seed the reviewer picker with — but only when
// that owner is one of the assignable PCPs (the same set the combobox offers as
// options). A non-PCP owner returns null so it is never shown, selected, or
// assigned as the PCP reviewer; this keeps the control's value, its display,
// and the assign target identical.
export function reviewerSeedFromOwner(
  ownerReference: string | null,
  assignablePcpReferences: readonly (string | undefined)[]
): string | null {
  if (!ownerReference) {
    return null
  }
  return assignablePcpReferences.includes(ownerReference)
    ? ownerReference
    : null
}

export function DocumentReviewToolbar({
  docRef,
  task,
  onUpdate,
  onComplete,
}: DocumentReviewToolbarProps): JSX.Element {
  const isReadOnly = useIsReadOnly()
  const openDeleteConfirmation = useDeleteConfirmationStore(
    (state) => state.open
  )

  // The owner control represents the PCP reviewer. Seed it from the task's
  // persisted owner, but only when that owner is one of the assignable PCPs —
  // the same set the combobox offers as options. A non-PCP owner falls through
  // to the placeholder and can never be displayed, selected, or assigned here,
  // so this control's value, its display, and the assign target stay identical.
  // Reusing the combobox's PCP query (deduped by React Query) avoids a separate
  // per-owner role lookup.
  const assignablePcps = usePractitionersInternal([Role.PCP])
  const assignablePcpReferences = useMemo(
    () => assignablePcps.map((p) => p.reference.reference),
    [assignablePcps]
  )
  const ownerReference = task.ownerPractitioner?.reference ?? null
  const reviewerSeed = reviewerSeedFromOwner(
    ownerReference,
    assignablePcpReferences
  )

  const [selectedPractitioner, setSelectedPractitioner] = useState<
    string | null
  >(reviewerSeed)
  const [selectedPriority, setSelectedPriority] = useState<
    Task['priority'] | null
  >(null)
  const [splitOverlayOpen, setSplitOverlayOpenRaw] = useState(false)
  // Track whether the split dialog has ever been opened so we can keep it
  // mounted after first open. This lets Radix drive its own unmount via the
  // exit animation instead of having the whole Suspense boundary ripped out
  // of the tree when `splitOverlayOpen` flips to false.
  const [splitOverlayEverOpened, setSplitOverlayEverOpened] = useState(false)
  const setSplitOverlayOpen = useCallback((open: boolean) => {
    if (open) {
      setSplitOverlayEverOpened(true)
    }
    setSplitOverlayOpenRaw(open)
  }, [])

  // Re-seed the reviewer whenever the document changes or the PCP owner
  // resolves (the membership check above is async). An explicit user selection
  // persists because the seed key is otherwise stable.
  const reviewerSeedChanged = useDidChange(
    `${docRef?.reference?.reference ?? ''}|${reviewerSeed ?? ''}`
  )
  if (reviewerSeedChanged) {
    setSelectedPractitioner(reviewerSeed)
  }

  const docRefChanged = useDidChange(docRef?.reference?.reference)
  if (docRefChanged && docRef) {
    setSelectedPriority(null)
  }

  const patchDocument = usePatchMutation<ExtendedDocumentReference>()
  const patchTask = usePatchMutation<DocumentReviewTask>()
  const transactionMutation = useTransactionMutation()
  const { mutate: mutateTransaction } = transactionMutation
  const currentPractitioner = useCurrentPractitioner()
  const { roles } = useCurrentPractitionerPerms()
  const isPCP = roles.includes(Role.PCP)
  // Only offer folders the filer can themselves open, so nobody files a
  // document into a folder that then disappears from their own view.
  const folders = useFolders().filter((folder) =>
    currentPractitioner.isRole(...folder.roles)
  )
  const markRelatedInboxItemsAsDone = useMarkRelatedInboxItemsAsDone({
    silent: true,
  })

  const { data: signatures = [] } = useSearch(
    Signature,
    Signature.queryByTarget(docRef.reference.reference)
  )

  const hasCurrentPractitionerSigned = useMemo(() => {
    if (!isPCP) {
      return false
    }
    return ExtendedDocumentReference.hasPractitionerSigned(
      signatures,
      currentPractitioner.reference
    )
  }, [isPCP, signatures, currentPractitioner.reference])

  const isReviewed = docRef.isConfirmed && task?.isDone
  const isActionLoading =
    patchDocument.isPending ||
    patchTask.isPending ||
    transactionMutation.isPending

  const documentForm = useDocumentForm({
    docRef,
    task,
    onSubmitSuccess: onUpdate,
    isActionLoading,
  })

  const validateAndGetFormValues = useCallback(async () => {
    if (documentForm.isDirty) {
      const isValid = await documentForm.form.trigger()
      if (!isValid) {
        return null
      }
    }
    return documentForm.form.getValues()
  }, [documentForm])

  const applyFormChangesToDocument = useCallback(
    (
      doc: ExtendedDocumentReference,
      formValues: DocumentReferenceFormValues
    ) => {
      applyFormValuesToDocument(doc, formValues, documentForm.availableOrders)
    },
    [documentForm.availableOrders]
  )

  const handleSuccessWithFormReset = useCallback(
    (formValues: DocumentReferenceFormValues) => {
      if (documentForm.isDirty) {
        documentForm.form.reset(formValues)
      }
      onUpdate()
      onComplete()
    },
    [documentForm, onUpdate, onComplete]
  )

  const handleMarkComplete = useCallback(async () => {
    const formValues = await validateAndGetFormValues()
    if (!formValues) {
      return
    }

    mutateTransaction(
      (transaction) => {
        transaction.patch(docRef, (doc) =>
          applyFormChangesToDocument(doc, formValues)
        )
        transaction.patch(task, (t) => {
          t.state = 'done'
          if (formValues.subject) {
            t.participant = formValues.subject
          }
        })
      },
      {
        onSuccess: () => {
          markRelatedInboxItemsAsDone(
            createTaskFilter([
              {
                taskReferenceString: task.reference.reference,
                state: 'done',
              },
            ])
          )
          toast.success('Document marked complete.')
          handleSuccessWithFormReset(formValues)
        },
        onError: (error) => {
          console.error('Failed to mark document as complete', error)
          toast.error('Failed to mark document as complete')
        },
      }
    )
  }, [
    validateAndGetFormValues,
    mutateTransaction,
    docRef,
    applyFormChangesToDocument,
    task,
    markRelatedInboxItemsAsDone,
    handleSuccessWithFormReset,
  ])

  const handleAssignToSelected = useCallback(async () => {
    if (!selectedPractitioner) {
      toast.error('Please select a practitioner')
      return
    }

    const formValues = await validateAndGetFormValues()
    if (!formValues) {
      return
    }

    mutateTransaction(
      (transaction) => {
        transaction.patch(docRef, (doc) =>
          applyFormChangesToDocument(doc, formValues)
        )
        transaction.patch(task, (t) => {
          t.state = 'in-progress'
          t.ownerPractitioner = parseReferenceString(selectedPractitioner)
          if (selectedPriority) {
            t.priority = selectedPriority
          }
          if (formValues.subject) {
            t.participant = formValues.subject
          }
        })
      },
      {
        onSuccess: () => handleSuccessWithFormReset(formValues),
        onError: (error) => {
          console.error('Failed to assign document', error)
          toast.error('Failed to assign document')
        },
      }
    )
  }, [
    selectedPractitioner,
    selectedPriority,
    validateAndGetFormValues,
    mutateTransaction,
    docRef,
    applyFormChangesToDocument,
    task,
    handleSuccessWithFormReset,
  ])

  const handleSign = useCallback(async () => {
    const formValues = await validateAndGetFormValues()
    if (!formValues) {
      return
    }

    const signature = new Signature({
      target: docRef.reference,
      who: currentPractitioner.reference,
    })

    mutateTransaction(
      (transaction) => {
        transaction.patch(docRef, (doc) =>
          applyFormChangesToDocument(doc, formValues)
        )
        transaction.create(signature, {
          request: {
            ifNoneExist: queryTypesToString(
              Signature.queryByTargetAndWho(
                docRef.reference,
                currentPractitioner.reference
              )
            ),
          },
        })
        transaction.patch(task, (t) => {
          t.state = 'done'
          if (formValues.subject) {
            t.participant = formValues.subject
          }
        })
      },
      {
        onSuccess: () => {
          toast.success('Document signed successfully')
          markRelatedInboxItemsAsDone(
            createTaskFilter([
              {
                taskReferenceString: task.reference.reference,
                state: 'done',
              },
            ])
          )
          onUpdate()
          onComplete()
        },
        onError: (error) => {
          console.error('Failed to sign document', error)
          toast.error('Failed to sign document')
        },
      }
    )
  }, [
    validateAndGetFormValues,
    docRef,
    currentPractitioner.reference,
    mutateTransaction,
    applyFormChangesToDocument,
    task,
    markRelatedInboxItemsAsDone,
    onUpdate,
    onComplete,
  ])

  const handleDelete = useCallback(() => {
    openDeleteConfirmation({
      title: 'Delete document',
      description: 'Are you sure you want to delete this document?',
      onConfirm: () => {
        mutateTransaction(
          (transaction) => {
            transaction.patch(docRef, (doc) => {
              doc.markDeleted()
            })
            transaction.patch(task, (t) => {
              t.state = 'canceled'
            })
          },
          { onSuccess: () => onComplete() }
        )
      },
    })
  }, [openDeleteConfirmation, mutateTransaction, docRef, task, onComplete])

  const participants = useParticipants()
  const selectedSubject = documentForm.form.watch('subject')
  const hasSubject = !!selectedSubject
  const isDirty = documentForm.isDirty
  const hasMultiplePredictedSubjects = docRef.hasMultiplePredictedSubjects
  const canSplit =
    !isReadOnly &&
    !isReviewed &&
    DocumentReviewTask.getSplitBlockReason(docRef, task) === null

  const participantOptions = useMemo(
    () =>
      participants.map((participant) => ({
        label: <ParticipantOption participant={participant.reference} />,
        value: participant.id,
        keywords: participant.searchKeywords,
      })),
    [participants]
  )

  const orderOptions = useMemo(
    () =>
      documentForm.availableOrders?.map((order) => ({
        label: <OrderOption order={order.reference} />,
        value: order.id ?? '',
        keywords: order.searchKeywords ?? [],
      })) ?? [],
    [documentForm.availableOrders]
  )

  const suggestedParticipantOptions = useMemo((): SuggestedOption<string>[] => {
    const predicted = documentForm.predictedSubject
    if (!predicted) {
      return []
    }

    const participantId = resolveId(predicted.reference)
    if (!participantId) {
      return []
    }

    const participant = participants.find((p) => p.id === participantId)
    if (!participant) {
      return []
    }

    return [
      {
        label: <ParticipantOption participant={predicted.reference} />,
        value: participantId,
        keywords: participant.searchKeywords,
        tooltip: (
          <SuggestedOptionTooltip
            confidence={predicted.confidence}
            reason={predicted.reason}
          />
        ),
      },
    ]
  }, [documentForm.predictedSubject, participants])

  const suggestedOrderOptions = useMemo((): SuggestedMultiOption<string>[] => {
    const predicted = documentForm.predictedOrders
    if (!predicted?.length) {
      return []
    }

    const suggestions: SuggestedMultiOption<string>[] = []

    for (const p of predicted) {
      const orderId = resolveId(p.reference)
      if (!orderId) {
        continue
      }

      const order = documentForm.availableOrders?.find((o) => o.id === orderId)
      if (!order) {
        continue
      }

      suggestions.push({
        label: <OrderOption order={p.reference} />,
        value: orderId,
        keywords: order.searchKeywords ?? [],
        tooltip: (
          <SuggestedOptionTooltip confidence={p.confidence} reason={p.reason} />
        ),
      })
    }

    return suggestions
  }, [documentForm.predictedOrders, documentForm.availableOrders])

  const { data: idt } = useSearchOne(
    IDT,
    selectedSubject ? IDT.byParticipant(selectedSubject) : undefined
  )

  const suggestedPCPOptions = useMemo((): SuggestedOption<string | null>[] => {
    if (!idt) {
      return []
    }

    const pcpMember = idt.getMemberByRole(Role.PCP)
    if (!pcpMember?.practitioner.reference) {
      return []
    }

    return [
      {
        label: (
          <PractitionerOption
            practitioner={pcpMember.practitioner}
            isSelected={
              pcpMember.practitioner.reference === selectedPractitioner
            }
          />
        ),
        value: pcpMember.practitioner.reference,
        keywords: [],
        tooltip: <SuggestedOptionTooltip reason="Participant's PCP" />,
      },
    ]
  }, [idt, selectedPractitioner])

  return (
    <Form>
      <div className="border-b border-border bg-background px-pagex-default py-4 lg:px-pagex-lg">
        <div className="flex flex-col gap-4">
          {hasMultiplePredictedSubjects && (
            <div className="flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm">
              <IconAlertTriangle className="size-4 shrink-0 text-amber-600" />
              <span className="text-amber-800">
                AI detected multiple participants in this document.
              </span>
              {canSplit && (
                <Button
                  variant="link"
                  className="h-auto p-0 text-sm text-amber-800 underline"
                  onClick={() => setSplitOverlayOpen(true)}
                >
                  Split document
                </Button>
              )}
            </div>
          )}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <FormField
              control={documentForm.form.control}
              name="subject"
              render={({ field, fieldState }) => (
                <Field data-invalid={fieldState.invalid}>
                  <FieldLabel htmlFor={field.id}>Participant</FieldLabel>
                  <FieldContent>
                    <Combobox
                      options={participantOptions}
                      suggestedOptions={suggestedParticipantOptions}
                      suggestedLabel="AI Suggested"
                      onSelect={(value) => {
                        const selectedParticipant = value
                          ? (participants.find((p) => p.id === value)
                              ?.reference ?? null)
                          : null
                        field.onChange(
                          selectedParticipant as DocumentReferenceFormValues['subject']
                        )
                      }}
                      className="min-w-full"
                    >
                      <Button
                        id={field.id}
                        variant="outline"
                        onBlur={field.onBlur}
                        aria-invalid={field['aria-invalid']}
                        aria-describedby={field['aria-describedby']}
                        className="w-full justify-start"
                      >
                        {field.value ? (
                          <ParticipantNameplate participant={field.value} />
                        ) : (
                          <UserPlaceholderNameplate>
                            Select participant
                          </UserPlaceholderNameplate>
                        )}
                      </Button>
                    </Combobox>
                    <FieldErrorComponent errors={[fieldState.error]} />
                  </FieldContent>
                </Field>
              )}
            />

            <FormField
              control={documentForm.form.control}
              name="categories"
              render={({ field, fieldState }) => (
                <Field data-invalid={fieldState.invalid}>
                  <FieldLabel htmlFor={field.id}>Categories</FieldLabel>
                  <FieldContent>
                    <MultiSelect
                      options={DOCUMENT_CATEGORY_OPTIONS}
                      value={field.value ?? []}
                      onChange={(values) => field.onChange(values)}
                      onBlur={field.onBlur}
                      placeholder="Select categories…"
                      variant="outline"
                    />
                    <FieldErrorComponent errors={[fieldState.error]} />
                  </FieldContent>
                </Field>
              )}
            />

            {folders.length > 0 && (
              <FormField
                control={documentForm.form.control}
                name="folderId"
                render={({ field, fieldState }) => (
                  <Field data-invalid={fieldState.invalid}>
                    <FieldLabel htmlFor={field.id}>Folder</FieldLabel>
                    <FieldContent>
                      <ComboboxButton
                        id={field.id}
                        value={field.value ?? OTHER_FOLDER_VALUE}
                        onChange={(value) =>
                          field.onChange(
                            value === OTHER_FOLDER_VALUE ? null : value
                          )
                        }
                        onBlur={field.onBlur}
                        options={[
                          { value: OTHER_FOLDER_VALUE, label: 'Other' },
                          ...folders.map((folder) => ({
                            value: folder.id,
                            label: folder.name,
                          })),
                        ]}
                        className="w-full justify-start"
                      />
                      <FieldErrorComponent errors={[fieldState.error]} />
                    </FieldContent>
                  </Field>
                )}
              />
            )}

            <FormField
              control={documentForm.form.control}
              name="orders"
              render={({ field, fieldState }) => (
                <Field data-invalid={fieldState.invalid}>
                  <FieldLabel htmlFor={field.id}>Orders</FieldLabel>
                  <FieldContent>
                    <ComboboxMulti
                      options={orderOptions}
                      suggestedOptions={suggestedOrderOptions}
                      suggestedLabel="AI Suggested"
                      values={field.value ?? []}
                      onSelect={(values: string[]) => field.onChange(values)}
                      className="w-[400px]"
                    >
                      <Button
                        id={field.id}
                        variant="outline"
                        aria-invalid={field['aria-invalid']}
                        aria-describedby={field['aria-describedby']}
                        onBlur={field.onBlur}
                        disabled={!selectedSubject}
                        className="w-full justify-start"
                      >
                        {(field.value ?? []).length > 0
                          ? `${(field.value ?? []).length} ${pluralize('order', (field.value ?? []).length)} selected`
                          : 'Link Orders'}
                      </Button>
                    </ComboboxMulti>
                    <FieldErrorComponent errors={[fieldState.error]} />
                  </FieldContent>
                </Field>
              )}
            />
          </div>

          {!isReadOnly && (
            <div className="flex flex-wrap items-center justify-end gap-2">
              <div className="mr-auto flex gap-2">
                {canSplit && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        type="button"
                        onClick={() => setSplitOverlayOpen(true)}
                      >
                        <IconScissors className="mr-1 size-4" />
                        Split
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      Split this document into separate documents for different
                      participants
                    </TooltipContent>
                  </Tooltip>
                )}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      type="button"
                      onClick={handleDelete}
                      disabled={documentForm.disableActions}
                    >
                      <IconTrash className="mr-1 size-4" />
                      Delete
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    Delete this document — removes it from the inbox and marks
                    it entered-in-error
                  </TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      type="button"
                      onClick={handleMarkComplete}
                      disabled={
                        documentForm.disableActions || !(!isReviewed || isDirty)
                      }
                    >
                      {isReviewed
                        ? isDirty
                          ? 'Save'
                          : 'Saved'
                        : 'Mark as Filed'}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    Files this document to the participant with selected
                    categories and linked orders
                  </TooltipContent>
                </Tooltip>
                {isPCP && hasSubject && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant={
                          hasCurrentPractitionerSigned ? 'outline' : 'default'
                        }
                        type="button"
                        onClick={handleSign}
                        disabled={
                          documentForm.disableActions ||
                          hasCurrentPractitionerSigned
                        }
                      >
                        {hasCurrentPractitionerSigned ? 'Signed' : 'Sign'}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      {hasCurrentPractitionerSigned
                        ? 'You have signed this document'
                        : 'Sign this document as PCP'}
                    </TooltipContent>
                  </Tooltip>
                )}
              </div>
              <TaskPriorityCombobox
                onSelect={setSelectedPriority}
                selected={selectedPriority ?? null}
              >
                <Button
                  variant="outline"
                  className="gap-2"
                  disabled={documentForm.disableActions || !hasSubject}
                >
                  <TaskPriorityIcon priority={selectedPriority ?? undefined} />
                  {selectedPriority
                    ? StandardTask.priorityText(selectedPriority)
                    : 'Priority'}
                </Button>
              </TaskPriorityCombobox>
              <ButtonGroup>
                <OwnerComboboxButton
                  value={selectedPractitioner}
                  onChange={setSelectedPractitioner}
                  disabled={documentForm.disableActions || !hasSubject}
                  idtRoles={[Role.PCP]}
                  suggestedOptions={suggestedPCPOptions}
                  suggestedLabel="Participant's IDT"
                />
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      onClick={handleAssignToSelected}
                      disabled={
                        documentForm.disableActions ||
                        !hasSubject ||
                        !selectedPractitioner
                      }
                    >
                      {documentForm.disableActions
                        ? 'Processing…'
                        : 'Assign to PCP for Review'}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    Assigns this document to the selected PCP for review and
                    follow-up
                  </TooltipContent>
                </Tooltip>
              </ButtonGroup>
            </div>
          )}
        </div>
      </div>
      {canSplit && splitOverlayEverOpened && (
        <Suspense fallback={null}>
          <SplitDocumentOverlay
            open={splitOverlayOpen}
            onOpenChange={setSplitOverlayOpen}
            docRef={docRef}
            task={task}
            onSplitComplete={() => {
              onUpdate()
              onComplete()
            }}
          />
        </Suspense>
      )}
    </Form>
  )
}
